import { createSelector } from "@reduxjs/toolkit";
import {
  CancellationSummary,
  CorpLodging,
  CorpRoomInfoProducts,
  HotelCancellationPolicyV2,
  Lodging,
  RoomInfoProducts,
} from "redmond";
import { IStoreState } from "../../../reducers/types";
import {
  getHotelShopCancellationSummary,
  getHotelShopChosenProduct,
  getHotelShopChosenRoomInfo,
  getHotelShopDateRange,
  getPackageSelectedLodging,
} from "../reducer";
import { getPackagedRoomComponent } from "../../package-identity/reducer/selectors";
import {
  toCancellationSummary,
  toHotelCancellationPolicyV2,
  toRoomInfoProducts,
  toSelectedLodging,
  toSelectedRoomRateId,
} from "./packageSnapshotAdapters";

export interface PackageHotelIdentity {
  selectedLodging: Lodging | CorpLodging | null | undefined;
  selectedRoom: RoomInfoProducts | CorpRoomInfoProducts | undefined;
  selectedRoomRateId: string | undefined;
  cancellationSummary: CancellationSummary | null | undefined;
  cancellationPolicyV2: HotelCancellationPolicyV2 | undefined;
  checkIn: string | undefined;
  checkOut: string | undefined;
}

/**
 * Resolves hotel/lodging selection data from whichever source is populated — v0's own hotel-shop
 * selection, or v2's `GetPackage` snapshot. `state.packageIdentity.data` (read via
 * `getPackagedRoomComponent`) is only ever populated under v2 — `pollPackageVersion`, its one
 * dispatcher, is only ever called behind the v2 flag — so presence of the v2 snapshot is a safe
 * proxy for "running under v2," the same way {@link getFlightTripIdentity} resolves tripId/
 * tripDetails without needing the flag directly.
 *
 * Centralizes what were previously 6 independently-duplicated `effectiveXxx` ternaries across
 * `ReviewHotelItinerary`/`DesktopPackagesBookWorkflow`/`MobilePackagesBookWorkflow`.
 *
 * Memoized via `createSelector` (same as the other selectors here) — without it, every call
 * rebuilds the v2-derived fields (`toSelectedLodging`/`toRoomInfoProducts`/etc.) into fresh object
 * literals, and since `mapStateToProps` re-runs on every Redux dispatch app-wide, those new
 * references would retrigger any `useEffect` that depends on them (e.g. Book's
 * `INITIALIZE_CHECKOUT_STATE` effect) on unrelated state changes.
 *
 * The `createSelector` call itself is deferred to first invocation (rather than built at module
 * scope) — some import orders reach this module mid-cycle (e.g.
 * `finalizePackageSaga -> store -> reducers -> ... -> CancellationPoliciesCard ->` here), where
 * `hotel-shop/reducer`'s own selector exports haven't been assigned yet; building the selector
 * eagerly would capture those `undefined`s. Node resolves the cycle at call time instead.
 */
let memoized: ((state: IStoreState) => PackageHotelIdentity) | undefined;

export const getPackageHotelIdentity = (
  state: IStoreState
): PackageHotelIdentity => {
  if (memoized == null) {
    memoized = createSelector(
      [
        getPackagedRoomComponent,
        getHotelShopChosenProduct,
        getHotelShopDateRange,
        getPackageSelectedLodging,
        getHotelShopChosenRoomInfo,
        getHotelShopCancellationSummary,
      ],
      (
        packagedRoomComponent,
        chosenProduct,
        hotelDates,
        packageSelectedLodging,
        hotelShopChosenRoomInfo,
        hotelShopCancellationSummary
      ): PackageHotelIdentity => {
        // `toSelectedLodging` returns a `Partial<Lodging>` from `@b2bportal/lodging-api` — a
        // structurally near-identical but nominally distinct type from redmond's `Lodging`/`CorpLodging`
        // (v0's shape). Every existing caller already casts through this same boundary (see
        // `initializeCheckoutState`'s `as unknown as Lodging`); this accessor does it once, here.
        const selectedLodging = (packageSelectedLodging ??
          (packagedRoomComponent
            ? toSelectedLodging(packagedRoomComponent)
            : undefined)) as Lodging | CorpLodging | null | undefined;

        const selectedRoom =
          hotelShopChosenRoomInfo ??
          (packagedRoomComponent
            ? toRoomInfoProducts(packagedRoomComponent)
            : undefined);

        const selectedRoomRateId =
          chosenProduct?.rateId?.value ??
          (packagedRoomComponent
            ? toSelectedRoomRateId(packagedRoomComponent)
            : undefined);

        const cancellationSummary =
          hotelShopCancellationSummary ??
          (packagedRoomComponent
            ? toCancellationSummary(packagedRoomComponent)
            : undefined);

        const cancellationPolicyV2Source =
          chosenProduct?.cancellationPolicyV2 ??
          packagedRoomComponent?.shopDetails?.cancellationPolicyV2;
        const cancellationPolicyV2 = cancellationPolicyV2Source
          ? toHotelCancellationPolicyV2(cancellationPolicyV2Source)
          : undefined;

        const checkIn =
          hotelDates?.from ?? packagedRoomComponent?.stayDates.from;
        const checkOut =
          hotelDates?.until ?? packagedRoomComponent?.stayDates.until;

        return {
          selectedLodging,
          selectedRoom,
          selectedRoomRateId,
          cancellationSummary,
          cancellationPolicyV2,
          checkIn,
          checkOut,
        };
      }
    );
  }

  return memoized(state);
};
